import {toAbsoluteUrl} from '_metronic/helpers'
import {FC, useState, useEffect} from 'react'
import Searchbar from '../components/Searchbar'
import {SubprocessorDataType} from '../components/Subprocessors'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

type Props = {
  subprocessors: SubprocessorDataType[]
}

const SubprocessorsTab: FC<Props> = ({subprocessors}) => {
  const [filteredSubprocessors, setFilteredSubprocessors] =
    useState<SubprocessorDataType[]>(subprocessors)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isNoSearchFound, setIsNoSearchFound] = useState(false)
  const [isTooltipShown, setIsTooltipShown] = useState(false)

  useEffect(() => {
    if (isTooltipShown) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [isTooltipShown])

  useEffect(() => {
    setFilteredSubprocessors(subprocessors)
  }, [subprocessors])

  useEffect(() => {
    const updatedData = subprocessors.filter((subprocessor) =>
      subprocessor.title.toLocaleLowerCase().includes(searchTerm.toLowerCase())
    )

    setFilteredSubprocessors(updatedData)
    setIsNoSearchFound(updatedData.length === 0)
  }, [searchTerm])

  return (
    <div className='w-100 d-flex flex-column flex-lg-row' style={{gap: '32px'}}>
      <div className='w-100 w-lg-25 d-flex flex-column' style={{gap: '12px', flexShrink: '0'}}>
        <div className='d-flex justify-content-between align-items-center'>
          <h3
            className='d-lg-none flex-fill text-custom-primary-gray mb-0'
            style={{fontSize: '20px', fontWeight: '600'}}
          >
            Subprocessors
          </h3>

          <div className='flex-lg-fill'>
            <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>
      </div>

      <div className='flex-fill d-flex flex-column' style={{gap: '20px'}}>
        {!isNoSearchFound ? (
          filteredSubprocessors.length > 0 &&
          filteredSubprocessors.map((subprocessor) => (
            <div
              key={subprocessor.id}
              className='d-flex align-items-start bg-white w-100'
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '24px',
              }}
            >
              <div className='flex-fill d-flex align-items-center' style={{gap: '24px'}}>
                <img
                  alt={`${subprocessor.title}-logo`}
                  src={toAbsoluteUrl(subprocessor.logoURL as string)}
                  className='object-fit-cover'
                  style={{height: '40px', width: '40px'}}
                />

                <div className='flex-fill d-flex flex-column ' style={{gap: '4px'}}>
                  <div
                    className='d-flex flex-column flex-md-row justify-content-between'
                    style={{gap: '4px'}}
                  >
                    <h4
                      className='text-custom-primary-gray lh-base fw-medium mb-0'
                      style={{fontSize: '16px'}}
                    >
                      {subprocessor.title}
                    </h4>

                    <div>
                      <span className='text-custom-secondary-gray' style={{fontSize: '16px'}}>
                        {subprocessor.hostingRegions.slice(0, 2).join(', ')}
                      </span>

                      {subprocessor.hostingRegions.length > 2 && (
                        <OverlayTrigger
                          placement='bottom-start'
                          popperConfig={{
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                options: {
                                  boundary: 'viewport',
                                },
                              },
                            ],
                          }}
                          overlay={
                            <Tooltip
                              id={`tooltip-${subprocessor.id}`}
                              style={
                                {
                                  '--bs-tooltip-max-width': '250px',
                                  whiteSpace: 'pre-line',
                                } as React.CSSProperties
                              }
                            >
                              <ul className='d-flex flex-column gap-1 m-0 p-0 ps-4'>
                                {subprocessor.hostingRegions
                                  .slice(2)
                                  .map((hostingRegion, index) => (
                                    <li
                                      key={index}
                                      className='text-start text-custom-secondary-gray'
                                      style={{fontSize: '14px'}}
                                    >
                                      {hostingRegion.trim()}
                                    </li>
                                  ))}
                              </ul>
                            </Tooltip>
                          }
                        >
                          <span
                            className='text-custom-secondary-gray'
                            style={{fontSize: '16px'}}
                            onMouseEnter={() => setIsTooltipShown(true)}
                            onMouseLeave={() => setIsTooltipShown(false)}
                          >
                            {`, +${subprocessor.hostingRegions.length - 2} more`}
                          </span>
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>

                  <span className='text-custom-secondary-gray' style={{fontSize: '14px'}}>
                    {subprocessor.category}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            className='w-100 h-100 d-flex align-items-center justify-content-center'
            style={{border: '1px solid #D1D5DB', borderRadius: '8px', paddingBlock: '24px'}}
          >
            <span
              className='text-custom-primary-graytext-custom-primary-gray fw-medium'
              style={{fontSize: '16px'}}
            >
              {subprocessors.length === 0
                ? 'No subprocessors added'
                : 'No matching subprocessors found'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubprocessorsTab
